import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActions,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import axiosInstance from '../../axiosInstance';

/**
 * Component for searching and adding music items to collection
 */
const MusicSearch = ({ onAddMusic, isWishlist = false, initialQuery = '', hideSearchBox = false }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  
  // If initialQuery is provided, perform search automatically
  useEffect(() => {
    if (initialQuery) {
      setSearchQuery(initialQuery);
      // Use a timeout to ensure the component is fully mounted
      const timer = setTimeout(() => {
        handleSearch({ preventDefault: () => {} });
      }, 100);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  /**
   * Handle search for music using API
   */
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError('');
    setSearchResults([]);
    
    console.log('Searching for music with query:', searchQuery);
    
    try {
      const response = await axiosInstance.get(`/music/search?query=${encodeURIComponent(searchQuery)}`);
      console.log('Music search response:', response);
      
      if (response.data && Array.isArray(response.data)) {
        setSearchResults(response.data);
        if (response.data.length === 0) {
          setError('No music found. Try a different search term or use manual entry.');
        }
      } else if (response.data && response.data.error) {
        console.error('Music API error:', response.data.error);
        setError(`Error from music database: ${response.data.error}`);
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Unexpected response format from the server. Try manual entry instead.');
      }
    } catch (err) {
      console.error('Error searching for music:', err);
      setError(err.response?.data?.error || 'Error searching for music. Please try again or use manual entry.');
      setShowManualEntry(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle adding a music item to collection or wishlist
   */
  const handleAddMusic = async (music) => {
    try {
      // Create a music object with required fields for the API
      const musicToAdd = {
        name: (music.name || 'Unknown Album').trim(),
        artist: (music.artist || 'Unknown Artist').trim(),
        format: (music.format || 'CD').trim(),
        condition: music.condition || 'Very Good (VG)',
        edition: music.type === 'Album' ? 'Standard Release' : (music.type || 'Standard Release'),
        imageUrl: music.imageUrl || '',
        priority: isWishlist ? 'Medium' : undefined,
        listened: false,
        notes: '',
        price: '',
        year: music.year ? String(music.year).substring(0, 4) : '',
        genre: music.genre || 'Unknown Genre',
        label: music.label || 'Unknown Label',
        // Ensure tracks is properly formatted
        tracks: Array.isArray(music.tracks) 
          ? music.tracks.map(track => 
              typeof track === 'string' 
                ? track 
                : `${track.position || ''}. ${track.title || ''}`.trim()
            ).filter(t => t)
          : []
      };

      console.log('Adding music item with data:', musicToAdd);
      
      // Call the parent's onAddMusic function with the formatted data
      await onAddMusic(musicToAdd);
      
      // Show success message
      enqueueSnackbar(
        `${musicToAdd.name} by ${musicToAdd.artist} has been added to your ${isWishlist ? 'wishlist' : 'collection'}.`,
        { variant: 'success' }
      );
      
      // Reset the search after successful addition
      setSearchQuery('');
      setSearchResults([]);
      
    } catch (err) {
      console.error('Error adding music item:', err);
      
      // Show error message
      enqueueSnackbar(
        err.response?.data?.message || 'Failed to add music item. Please try again.',
        { variant: 'error' }
      );
    }
  };

  /**
   * Create a manual entry for music that can't be found via search
   */
  const handleManualEntry = () => {
    const manualEntry = {
      name: searchQuery,
      artist: '',
      year: '',
      genre: '',
      label: '',
      tracks: '',
      imageUrl: '',
      format: 'Not Specified',
      condition: 'Not Specified',
      edition: 'Not Specified',
      priority: isWishlist ? 'Medium' : undefined,
      listened: false,
      price: ''
    };
    
    onAddMusic(manualEntry);
  };

  return (
    <Box sx={{ mt: 3 }}>
      {!hideSearchBox && (
        <Box component="form" onSubmit={handleSearch} sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 2, flex: 1, minWidth: 250 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Search for music"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Album, artist, or song name"
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ minWidth: 120 }}
            >
              Search
            </Button>
          </Box>
          <Button 
            variant="outlined" 
            onClick={() => setShowManualEntry(!showManualEntry)}
            startIcon={<AddIcon />}
            sx={{ minWidth: 180 }}
          >
            {showManualEntry ? 'Hide Manual Entry' : 'Add Manually'}
          </Button>
        </Box>
      )}
      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {searchResults.length > 0 ? (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            Search Results ({searchResults.length} found)
          </Typography>
          <Grid container spacing={3}>
            {searchResults.map((music, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={music.id || index}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  }
                }}>
                  {music.imageUrl ? (
                    <CardMedia
                      component="img"
                      height="200"
                      image={music.imageUrl}
                      alt={music.name}
                      sx={{ 
                        objectFit: 'contain', 
                        bgcolor: 'background.paper', 
                        p: 2,
                        maxHeight: 250
                      }}
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        height: 200, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: 'background.paper',
                        p: 2
                      }}
                    >
                      <MusicNoteIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Album Title with better context */}
                    <Typography 
                      gutterBottom 
                      variant="subtitle1" 
                      component="div" 
                      sx={{ 
                        fontWeight: 'bold',
                        lineHeight: 1.2,
                        mb: 0.5
                      }}
                      title={music.name}
                    >
                      {music.name}
                    </Typography>
                    
                    {/* Artist with "by" prefix for clarity */}
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      gutterBottom 
                      noWrap
                      sx={{ mb: 1 }}
                    >
                      by {music.artist}
                    </Typography>
                    
                    {/* Album type and year for better context */}
                    {(music.type || music.year) && (
                      <Typography 
                        variant="caption" 
                        color="primary.main" 
                        display="block" 
                        sx={{ 
                          fontWeight: 'medium',
                          mb: 1,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}
                      >
                        {music.type || 'Album'} {music.year && `• ${music.year}`}
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
                      {music.format && (
                        <Chip 
                          label={music.format} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {music.country && (
                        <Chip 
                          label={music.country} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                      {music.tracks && music.tracks.length > 0 && (
                        <Chip 
                          label={`${music.tracks.length} tracks`} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                    
                    {music.label && music.label !== 'Unknown Label' && (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        Label: {music.label}
                      </Typography>
                    )}
                    
                    {music.genre && music.genre !== 'Unknown Genre' && (
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Genre: {music.genre}
                      </Typography>
                    )}
                    
                    {/* Show first few track names for better identification */}
                    {music.tracks && music.tracks.length > 0 && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        display="block"
                        sx={{ 
                          mt: 1,
                          fontStyle: 'italic',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Tracks: {music.tracks.slice(0, 3).map(track => 
                          typeof track === 'string' ? track : track.title
                        ).join(', ')}{music.tracks.length > 3 ? '...' : ''}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ mt: 'auto', justifyContent: 'center', pb: 2 }}>
                    <Button 
                      size="small" 
                      variant="contained"
                      color="primary"
                      onClick={() => handleAddMusic(music)}
                      startIcon={<AddIcon />}
                      fullWidth
                      sx={{ mx: 1 }}
                    >
                      Add {isWishlist ? 'to Wishlist' : ''}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        !loading && 
        searchQuery.trim() !== '' && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>
              No results found. Would you like to add this item manually?
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleManualEntry}
              sx={{ mt: 1 }}
            >
              Add Manual Entry
            </Button>
          </Box>
        )
      )}
    </Box>
  );
};

export default MusicSearch;
